import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  TickerChart,
  type TickerChartDataPoint,
} from "@bloomberg/media-ui-markets";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import styles from "./wrappedPerformanceChart.module.css";
import TickerChartLegend from "~components/TickerChartLegend";
import {
  filterSelectedTickers,
  getChartTickerColor,
} from "~components/WatchlistComparisonChart/utils";
import { TickerChartData } from "~types/octagon";
import { TickerChartLegendData } from "~types/types";
import { addPageAction } from "~utils/newrelic/newrelicBrowser";
import { useOnDesktop } from "~hooks/useScreenBreakpoints";

type WrappedPerformanceChartProps = {
  chartData: TickerChartData[];
  chartCaption?: string;
};

export function WrappedPerformanceChart({
  chartData,
  chartCaption,
}: WrappedPerformanceChartProps) {
  const [tickerLegends, setTickerLegends] = useState<TickerChartLegendData[]>(
    []
  );
  const [hoveredChartData, setHoveredChartData] = useState<
    TickerChartDataPoint | undefined
  >(undefined);

  const onDesktop = useOnDesktop();

  // Add color and default fields to incoming chart data
  const coloredChartData = useMemo(() => {
    return chartData.map((entry, index) => ({
      ...entry,
      id: String(entry.id),
      color: getChartTickerColor(index),
      countryCode: entry.countryCode ?? "",
      exchangeCode: entry.exchangeCode ?? "",
    }));
  }, [chartData]);

  // Initialize legend with all tickers selected
  useEffect(() => {
    setTickerLegends(
      coloredChartData.map((entry) => ({
        id: entry.id,
        name: entry.id,
        color: entry.color,
        checked: true,
      }))
    );
  }, [coloredChartData]);

  const handleTickerSelectionChange = useCallback(
    (tickerId: string, isChecked: boolean) => {
      if (isChecked) {
        trackClickEvent(
          "quote_add_compare",
          "add-compare",
          "click",
          "Watchlist Performance Module"
        );
      } else {
        trackClickEvent(
          "quote_remove_compare",
          "remove-compare",
          "click",
          "Watchlist Performance Module"
        );
      }

      setTickerLegends((prev) => {
        const updated = prev.map((legend) =>
          legend.id === tickerId ? { ...legend, checked: isChecked } : legend
        );

        const numChecked = updated.filter((l) => l.checked).length;

        // Prevent all deselected
        return numChecked === 0 ? prev : updated;
      });
      addPageAction("watchlistWrappedTickerChange", {
        tickerId,
        isChecked,
      });
    },
    []
  );

  const handleTickerRemoval = useCallback((tickerId: string) => {
    setTickerLegends((prev) => prev.filter((legend) => legend.id !== tickerId));
  }, []);

  const filteredChartData = useMemo(() => {
    return filterSelectedTickers(tickerLegends, coloredChartData).map(
      (entry) => ({
        ...entry,
        id: String(entry.id),
      })
    );
  }, [tickerLegends, coloredChartData]);

  const renderMode = filteredChartData.length === 1 ? "price" : "percentage";
  const showGradient = filteredChartData.length === 1;

  return (
    <div>
      <div className={styles.performanceChartTitle}>Performance Chart</div>

      <TickerChart
        tickerSnapshots={filteredChartData}
        renderMode={renderMode}
        showGradient={showGradient}
        pricePrecision={2}
        enableDragInteraction
        handleChartHoverInteraction={setHoveredChartData}
        width={830}
        height={420}
        {...(!onDesktop && {
          xAxisTickSize: 4,
          yAxisTickSize: 4,
        })}
        showPreviousPrice={true}
        xAxisInterval="hour"
      />

      {chartCaption && (
        <div className={styles.caption}>{`${chartCaption}`}</div>
      )}

      <div className={styles.legendWrapper}>
        <TickerChartLegend
          className={styles.customLegendWrapper}
          hoveredDataPoint={hoveredChartData}
          tickerLegendData={tickerLegends}
          handleTickerSelectionChange={handleTickerSelectionChange}
          handleTickerRemoval={handleTickerRemoval}
        />
      </div>
    </div>
  );
}
